import { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import moment from 'moment';
import GppMaybeIcon from '@mui/icons-material/GppMaybe';
import { useAuth0 } from '@auth0/auth0-react';
import {
  Box,
  CircularProgress,
  FormControl,
  FormControlLabel,
  FormGroup,
  FormLabel,
  Grid,
  Typography,
} from '@mui/material';
import Checkbox from '@mui/material/Checkbox';

import { FBoxContainer, FHAutocomplete } from '../../../../components/base';
import TPActivationChart from './TPActivationChart';
import { clientActions, treatmentPlanActions } from '../../../../store';
import {
  FORTA_BLUE,
  FORTA_PURPLE,
  FORTA_WHITE,
  FORTA_SKY_BLUE,
  FORTA_ORANGE,
} from '../../../../utils/colors';
import { transformActivationData } from './utils';
import { TreatmentPlanStatus } from '../../../../components/layout/treatment-plan/constants/general';
import FHAntdDateRangePicker from '../../../../components/base/FHAntdDateRangePicker';
import { disabledFutureDate, history } from '../../../../helpers';
import PageContainer from '../../../../components/layout/PageContainer';

import type { RootState } from '../../../../store';
import type { Moment } from 'moment';
import type { ChangeEvent, HTMLAttributes } from 'react';
import type { IClient, IOption } from '../../../../utils/types';

enum DateRange {
  PastWeek = 7,
  PastMonth = 30,
  Past3Months = 90,
  Past6Months = 180,
  Past1Year = 365,
}

interface TPActivationLogsProps {
  showClientsDropdown?: boolean;
  loadData?: boolean;
  client?: IOption | null;
}

const TPActivationLogs = ({
  client = null,
  showClientsDropdown = true,
  loadData = true,
}: TPActivationLogsProps) => {
  const dispatch = useDispatch();
  const { getAccessTokenSilently } = useAuth0();
  const { list: clients = [], loading: clientLoading } = useSelector(
    (state: RootState) => state.clients,
  );
  const {
    list: treatmentPlans,
    tpStatusLoading,
    loading: tpLoading,
    tpStatusIdByLabel,
    activationLogLoading,
  } = useSelector((state: RootState) => state.treatmentPlans);
  const { client: selectedClientFromNavigation } = history.location.state || {};

  const [selectedClient, setSelectedClient] = useState<IOption | null>(
    client || selectedClientFromNavigation || null,
  );
  const [startDate, setStartDate] = useState<Moment>(
    moment().subtract(DateRange.Past3Months, 'days'),
  );
  const [endDate, setEndDate] = useState<Moment>(moment());
  const [selectedColor, setSelectedColor] = useState<string>(FORTA_SKY_BLUE);

  const clientOptions = useMemo(() => {
    if (!clients.length) return [];

    return clients.map((client: IClient) => ({
      id: client.id,
      label: `${client.first_name} ${client.last_name}`,
    }));
  }, [clients.length]);

  useEffect(() => {
    dispatch(treatmentPlanActions.clearTreatmentPlanState());
    return () => {
      dispatch(treatmentPlanActions.clearTreatmentPlanState());
    };
  }, []);

  useEffect(() => {
    if (loadData && !selectedClient && client) {
      setSelectedClient(client);
    }
  }, [loadData, client]);

  useEffect(() => {
    if (!loadData) return;
    dispatch(treatmentPlanActions.getTreatmentPlanStatuses({ getAccessTokenSilently }));
    return () => {
      dispatch(treatmentPlanActions.clearTreatmentPlan());
    };
    // eslint-disable
  }, [loadData]);

  useEffect(() => {
    if (clients?.length || !loadData) return;
    dispatch(clientActions.getList({ getAccessTokenSilently }));
  }, [clients?.length, loadData]);

  useEffect(() => {
    if (!selectedClient || !loadData) return;
    // Fetch treatment plans for selected client
    dispatch(
      treatmentPlanActions.getTreatmentPlanByQuery({
        query: { clientId: selectedClient.id, with_activation_logs: true },
        getAccessTokenSilently,
      }),
    );
  }, [selectedClient, loadData]);

  const handleSelectStartDateTime = (value: Moment) => {
    if (!value) return;
    setStartDate(value.startOf('day'));
  };

  const handleSelectEndDateTime = (value: Moment) => {
    if (!value) return;
    setEndDate(value.endOf('day'));
  };

  const dateRender = (date: Date) => {
    const current = moment(date);
    if (
      startDate &&
      endDate &&
      current.isSameOrAfter(startDate, 'day') &&
      current.isSameOrBefore(endDate, 'day')
    ) {
      // Highlight the cell if within the range
      return (
        <div
          className="ant-picker-cell-inner"
          style={{
            fontWeight: 700,
          }}
        >
          {current.date()}
        </div>
      );
    }
    return <div className="ant-picker-cell-inner">{current.date()}</div>;
  };

  const filteredTPs = treatmentPlans?.filter(
    (tp) =>
      tp.status_id === tpStatusIdByLabel[TreatmentPlanStatus.Completed] ||
      tp.status_id === tpStatusIdByLabel[TreatmentPlanStatus.Readonly] ||
      tp.status_id === tpStatusIdByLabel[TreatmentPlanStatus.Editing],
  );

  const handleDateRangeChange = (value: [Moment, Moment] | null | [null, null]) => {
    if (!value) {
      setStartDate(moment().subtract(DateRange.Past3Months, 'days'));
      setEndDate(moment());
      return;
    }

    if (value[0]) {
      handleSelectStartDateTime(value[0]);
    }
    if (value[1]) {
      handleSelectEndDateTime(value[1]);
    }
  };

  return (
    <PageContainer sx={{ px: 3, pt: 2 }}>
      <Box sx={{ mb: 3, py: 0 }}>
        <Grid container spacing={4} alignItems="center">
          {showClientsDropdown && (
            <Grid item xs={12} sm={4} md={3} lg={3}>
              <Typography fontWeight="bold" textAlign="left" sx={{ mb: 1 }}>
                Client
              </Typography>
              <FHAutocomplete
                inputHeight={40}
                sx={{ flexGrow: 1, boxShadow: 'none' }}
                placeholder="Select client"
                clearIcon={false}
                coloredBackground
                disabled={tpStatusLoading || clientLoading}
                isOptionEqualToValue={(option: any, value: any) => option.id === value.id}
                renderOption={(props: HTMLAttributes<HTMLLIElement>, option: IOption) => {
                  return (
                    <li {...props} key={option.id}>
                      {option.label}
                    </li>
                  );
                }}
                options={clientOptions ?? []}
                value={selectedClient}
                onChange={(_: ChangeEvent<HTMLSelectElement>, newValue: IOption | null) => {
                  setSelectedClient(newValue);
                }}
              />
            </Grid>
          )}
          <Grid item xs={12} sm={6} md={3}>
            <Typography fontWeight="bold" textAlign="left" sx={{ mb: 1 }}>
              Start - End Date
            </Typography>
            <FHAntdDateRangePicker
              coloredBackground
              format={'MM/DD/YYYY'}
              fullWidth
              changeOnBlur={true}
              allowClear={false}
              value={[startDate, endDate]}
              disabled={clientLoading || !selectedClient}
              dateRender={dateRender}
              disabledDate={disabledFutureDate}
              onChange={handleDateRangeChange}
              onCalendarChange={handleDateRangeChange}
              sx={{ minWidth: '240px', boxShadow: 'none' }}
            />
          </Grid>
        </Grid>
      </Box>
      <FBoxContainer sx={{ flexGrow: 0 }}>
        <Box
          width={'100%'}
          sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}
        >
          <Box>
            <Typography
              textAlign="left"
              fontSize={18}
              fontWeight={600}
              color={FORTA_BLUE.primary}
              mt={-3}
            >
              TP Activation
            </Typography>
            {filteredTPs?.length ? (
              <Typography fontSize={12} fontWeight={500}>
                Adjust the start and end dates above to expand or shrink the timeline.
              </Typography>
            ) : null}
          </Box>
          <FormControl component="fieldset" sx={{ ml: 2 }}>
            <FormLabel
              sx={{ textAlign: 'left', fontSize: 12, fontWeight: 600, color: FORTA_BLUE.primary }}
            >
              Timeline Color
            </FormLabel>
            <FormGroup row>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedColor === FORTA_SKY_BLUE}
                    onChange={() => setSelectedColor(FORTA_SKY_BLUE)}
                    size="small"
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography sx={{ fontSize: 12 }}>Blue</Typography>
                  </Box>
                }
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedColor === FORTA_PURPLE}
                    onChange={() => setSelectedColor(FORTA_PURPLE)}
                    size="small"
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography sx={{ fontSize: 12 }}>Purple</Typography>
                  </Box>
                }
              />
              <FormControlLabel
                control={
                  <Checkbox
                    checked={selectedColor === FORTA_ORANGE}
                    onChange={() => setSelectedColor(FORTA_ORANGE)}
                    size="small"
                  />
                }
                label={
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Typography sx={{ fontSize: 12 }}>Orange</Typography>
                  </Box>
                }
              />
            </FormGroup>
          </FormControl>
        </Box>
        {tpLoading ? (
          <Box
            sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: 200 }}
          >
            <CircularProgress />
          </Box>
        ) : filteredTPs?.length ? (
          <TPActivationChart
            activationLogLoading={activationLogLoading}
            startDate={startDate.toISOString()}
            endDate={endDate.toISOString()}
            tpActivationData={transformActivationData(filteredTPs ?? [])}
            selectedClientId={selectedClient?.id}
            selectedClientName={selectedClient?.label}
            timelineBackgroundColor={selectedColor}
          />
        ) : (
          <Box sx={{ p: 2, backgroundColor: FORTA_WHITE, borderRadius: 1, mt: 1.5 }}>
            {selectedClient ? (
              <>
                <GppMaybeIcon sx={{ color: FORTA_BLUE.primary }} />
                <Typography>No treatment plans found for the selected client</Typography>
              </>
            ) : (
              <Typography>
                Select a client to view the treatment plan activation summary.
              </Typography>
            )}
          </Box>
        )}
      </FBoxContainer>
    </PageContainer>
  );
};

export default TPActivationLogs;
